import type { ReactNode } from 'react';
import { Link } from 'wouter';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

import { LanguagePill } from '@/components/chrome/LanguagePill';

interface AuthPageLayoutProps {
  /** Omitted by pages that carry their own heading inside the content. */
  title?: string;
  /**
   * Tab title. Defaults to `title`; pass it explicitly on pages that render
   * their own heading and so leave `title` unset, otherwise they inherit
   * whatever title the previously visited page set.
   */
  documentTitle?: string;
  /**
   * Heading alignment. Registration keeps the centred treatment; login sets
   * 'left' so the heading lines up with the cards beneath it.
   */
  align?: 'center' | 'left';
  subtitle?: ReactNode;
  /** Extra line under the subtitle, such as the link to the other auth page. */
  aside?: ReactNode;
  children: ReactNode;
}

/**
 * Shell shared by the login and registration pages so both start at the same
 * vertical offset and share one chrome. Deliberately minimal — no marketing
 * nav, to avoid giving people a way out mid sign-up.
 */
export function AuthPageLayout({
  title,
  documentTitle,
  align = 'center',
  subtitle,
  aside,
  children,
}: AuthPageLayoutProps) {
  const { t } = useTranslation('footer');
  return (
    <div className="min-h-screen bg-surface-2 flex flex-col">
      <Helmet>
        {/*
          Set explicitly: without a title here, navigating in from a page that
          sets one (e.g. /my-bookings) leaves that stale title in the tab.
        */}
        {(documentTitle ?? title) && <title>{documentTitle ?? title}</title>}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="flex items-center justify-between px-4 py-4 sm:px-6">
        <Link href="/" className="inline-flex items-center">
          <img src="/images/logo_horizontal_transparent.png" alt="Sloty" className="h-8 w-auto" />
        </Link>
        <LanguagePill />
      </header>

      <main className="flex flex-1 flex-col items-center px-4 py-6 lg:py-8">
        {(title || subtitle || aside) && (
          <div className={`mb-6 w-full max-w-5xl ${align === 'left' ? 'text-left' : 'text-center'}`}>
            {title && <h1 className="text-h1 text-ink">{title}</h1>}
            {subtitle && <p className="mt-1.5 text-body text-soft">{subtitle}</p>}
            {aside && <p className="text-soft mt-2 text-sm">{aside}</p>}
          </div>
        )}

        <div className="w-full max-w-5xl">{children}</div>

      </main>

      <footer className="mt-auto border-t border-line bg-surface">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-3 px-4 py-5 sm:flex-row sm:justify-between sm:px-6">
          <p className="text-body-xs text-text-muted">
            &copy; {new Date().getFullYear()} Sloty. {t('rights')}
          </p>
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link href="/privacy" className="text-body-xs text-soft hover:text-ink">
              {t('privacy')}
            </Link>
            <Link href="/terms" className="text-body-xs text-soft hover:text-ink">
              {t('terms')}
            </Link>
            <Link href="/cookies" className="text-body-xs text-soft hover:text-ink">
              {t('cookies')}
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
